import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Card,
    Empty,
    Skeleton,
    Space,
    Switch,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import { InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, liveSettingsKeys } from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { SubsystemDraftActions } from '../components/SubsystemDraftActions';
import { PromptEditor } from '../shared/PromptEditor';
import {
    ProviderModelTempForm,
    ProviderModelTempValue,
    ProviderId,
} from '../shared/ProviderModelTempForm';

const { Text, Paragraph } = Typography;

interface AutoConciergeAIConfigRow {
    id?: number;
    config_key: string;
    system_prompt: string;
    user_prompt_template: string;
    is_active?: boolean | number;
    [k: string]: unknown;
}

interface AutoConciergeAiPayload {
    schemaVersion: 1;
    systemSettingsKeys: Record<string, string | undefined>;
    configTableRows: {
        auto_concierge_ai_config?: AutoConciergeAIConfigRow[];
    };
}

interface AutoConciergeKeyInfo {
    configKey: string;
    label: string;
    description: string;
}

// Mirrors the config_keys in auto_concierge_ai_config (SYR-105). The research
// prompt (commercial_profile) is deliberately NOT here — it runs inside the
// hypothesis crawl and writes to the shared research subject, so it belongs to
// The Hypothesis AI.
const AUTO_CONCIERGE_KEYS: AutoConciergeKeyInfo[] = [
    {
        configKey: 'pack_copy',
        label: 'Pack copy',
        description:
            "Writes the customer-facing email in the brand's own voice and recommends the incentive. Returns { researchSummary, voiceNotes, offerDescription, incentiveRationale, incentiveIsGiftCardDraw, customerEmailSubject, customerEmailBody, audienceGuidance }. The incentive must stay within AUTO_CONCIERGE_MAX_INCENTIVE_PERCENT, which is injected into the input rather than written into the prompt so the ceiling stays tunable.",
    },
    {
        configKey: 'covering_email',
        label: 'Covering email',
        description:
            "Writes Steve's note to the brand contact. Runs second, with the incentive already fixed, so the covering note can never name an offer the customer copy does not. Returns { subject, bodyHtml }. The body must carry every token below, each button token alone in its own paragraph.",
    },
];

const PLACEHOLDERS = (
    <span>
        <code>{'{input_text}'}</code> — the compiled brand input, built
        deterministically before the call: research fields, the survey facts
        (statements per respondent), the audiences with their cohorts, the
        category flags and the incentive ceiling. For{' '}
        <code>covering_email</code> it also carries the incentive already
        chosen by <code>pack_copy</code>.
    </span>
);

const COVERING_TOKENS = (
    <span>
        The rendered email substitutes <code>[[FIRST_NAME]]</code>,{' '}
        <code>[[SURVEY_LINK]]</code>, <code>[[TEST_LINK]]</code>,{' '}
        <code>[[CUSTOMER_EMAIL_COPY]]</code> and <code>[[CODE_LINK]]</code>.{' '}
        <code>[[TEST_LINK]]</code> and <code>[[CODE_LINK]]</code> render as
        full-width buttons, so each must sit alone in its own paragraph with no
        words wrapped around it. A missing token throws rather than sending a
        half-built email.
    </span>
);

function parseModelList(raw: string | undefined): Array<{ id: string; label?: string }> {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((entry: unknown) => {
                if (typeof entry === 'string') return { id: entry };
                if (entry && typeof entry === 'object') {
                    const e = entry as { id?: string; label?: string };
                    if (e.id) return { id: e.id, label: e.label };
                }
                return null;
            })
            .filter((m): m is { id: string; label?: string } => m !== null);
    } catch {
        return [];
    }
}

function guessProvider(modelId: string | undefined | null): ProviderId {
    if (!modelId) return 'anthropic';
    if (modelId.startsWith('claude') || modelId.startsWith('anthropic')) return 'anthropic';
    return 'openai';
}

function isActiveBool(v: boolean | number | undefined): boolean {
    return v === true || v === 1;
}

/**
 * Configure tab body for Auto-Concierge AI (SYR-105).
 *
 * Two flags, provider config, and the two pack prompts. Modelled on The
 * Hypothesis AI page.
 *
 * Editing prompts here replaces what used to require a database.sql UPDATE.
 * That matters: the pack prompts previously accreted nineteen appended CONCAT
 * patches and ended up contradicting themselves, which is exactly the failure
 * an in-app editor showing the whole prompt at once prevents.
 */
export const AutoConciergeAiConfigurePage: React.FC = () => {
    const editor = useSubsystemEditorContext<AutoConciergeAiPayload>();
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [anthropicModels, setAnthropicModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [openaiModels, setOpenaiModels] = useState<Array<{ id: string; label?: string }>>([]);
    const [googleModels, setGoogleModels] = useState<Array<{ id: string; label?: string }>>([]);

    useEffect(() => {
        if (!jwt) return;
        aiAdminApi
            .getSubsystem(jwt, 'models_providers')
            .then((mp) => {
                const keys = liveSettingsKeys(mp);
                setAnthropicModels(
                    parseModelList(keys.AVAILABLE_ANTHROPIC_MODELS),
                );
                setOpenaiModels(
                    parseModelList(keys.AVAILABLE_OPENAI_MODELS),
                );
                setGoogleModels(
                    parseModelList(keys.AVAILABLE_GOOGLE_MODELS),
                );
            })
            .catch(() => {
                // Non-fatal — admin can still type a model id manually.
            });
    }, [jwt]);

    if (editor.isLoading || !editor.payload) {
        return <Skeleton active paragraph={{ rows: 12 }} />;
    }

    const settings = editor.payload.systemSettingsKeys ?? {};
    const rows = editor.payload.configTableRows?.auto_concierge_ai_config ?? [];

    const setSetting = (key: string, value: string) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, [key]: value },
        }));
    };

    const setManySettings = (patch: Record<string, string>) => {
        editor.setPayload((prev) => ({
            ...prev!,
            systemSettingsKeys: { ...prev!.systemSettingsKeys, ...patch },
        }));
    };

    const upsertRow = (configKey: string, patch: Partial<AutoConciergeAIConfigRow>) => {
        editor.setPayload((prev) => {
            const list = prev!.configTableRows.auto_concierge_ai_config ?? [];
            const idx = list.findIndex((r) => r.config_key === configKey);
            const next =
                idx >= 0
                    ? list.map((r, i) => (i === idx ? { ...r, ...patch } : r))
                    : [
                          ...list,
                          {
                              config_key: configKey,
                              system_prompt: '',
                              user_prompt_template: '',
                              is_active: 1,
                              ...patch,
                          },
                      ];
            return {
                ...prev!,
                configTableRows: { ...prev!.configTableRows, auto_concierge_ai_config: next },
            };
        });
    };

    const findRow = (configKey: string): AutoConciergeAIConfigRow | undefined =>
        rows.find((r) => r.config_key === configKey);

    const enabled = (settings.AUTO_CONCIERGE_ENABLED ?? 'false') === 'true';
    const autoApproval = (settings.AUTO_PACK_APPROVAL ?? 'false') === 'true';

    const primaryValue: ProviderModelTempValue = {
        provider:
            (settings.AUTO_CONCIERGE_AI_PRIMARY_PROVIDER as ProviderId) ||
            guessProvider(settings.AUTO_CONCIERGE_AI_PRIMARY_MODEL),
        model: settings.AUTO_CONCIERGE_AI_PRIMARY_MODEL ?? '',
        temperature: Number(settings.AUTO_CONCIERGE_AI_PRIMARY_TEMPERATURE ?? 0.6),
    };

    const fallbackValue: ProviderModelTempValue = {
        provider:
            (settings.AUTO_CONCIERGE_AI_FALLBACK_PROVIDER as ProviderId) ||
            guessProvider(settings.AUTO_CONCIERGE_AI_FALLBACK_MODEL),
        model: settings.AUTO_CONCIERGE_AI_FALLBACK_MODEL ?? '',
        temperature: Number(settings.AUTO_CONCIERGE_AI_FALLBACK_TEMPERATURE ?? 0.6),
    };

    const handlePrimaryChange = (next: ProviderModelTempValue) => {
        setManySettings({
            AUTO_CONCIERGE_AI_PRIMARY_PROVIDER: next.provider,
            AUTO_CONCIERGE_AI_PRIMARY_MODEL: next.model,
            AUTO_CONCIERGE_AI_PRIMARY_TEMPERATURE: String(next.temperature),
        });
    };

    const handleFallbackChange = (next: ProviderModelTempValue) => {
        setManySettings({
            AUTO_CONCIERGE_AI_FALLBACK_PROVIDER: next.provider,
            AUTO_CONCIERGE_AI_FALLBACK_MODEL: next.model,
            AUTO_CONCIERGE_AI_FALLBACK_TEMPERATURE: String(next.temperature),
        });
    };

    if (rows.length === 0) {
        return (
            <Empty description="No auto_concierge_ai_config rows — has the SYR-105 seed SQL been run?" />
        );
    }

    return (
        <div>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
                <Text type="secondary">
                    The concierge pack: brand-voice customer email copy, the incentive
                    recommendation and the covering note.
                </Text>
                <SubsystemDraftActions
                    detail={editor.detail}
                    isDirty={editor.isDirty}
                    isSaving={editor.isSaving}
                    isPublishing={editor.isPublishing}
                    isDiscarding={editor.isDiscarding}
                    onSaveDraft={() => editor.saveDraft()}
                    onDiscardDraft={() => editor.discardDraft()}
                />
            </Space>

            <Card title="Status" size="small" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }} size={12}>
                    <Space align="start">
                        <Switch
                            checked={enabled}
                            onChange={(checked) =>
                                setSetting('AUTO_CONCIERGE_ENABLED', checked ? 'true' : 'false')
                            }
                            checkedChildren="enabled"
                            unCheckedChildren="disabled"
                        />
                        <Text type="secondary">
                            <code>AUTO_CONCIERGE_ENABLED</code> — whether drafts get MADE
                            automatically. Gates the wizard-completion trigger only. The admin
                            "Build a pack" button still works with this off, which is what makes
                            it safe to ship to production switched off and run packs by hand
                            first.
                        </Text>
                    </Space>

                    <Space align="start">
                        <Switch
                            checked={autoApproval}
                            onChange={(checked) =>
                                setSetting('AUTO_PACK_APPROVAL', checked ? 'true' : 'false')
                            }
                            checkedChildren="on"
                            unCheckedChildren="off"
                        />
                        <Text type="secondary">
                            <code>AUTO_PACK_APPROVAL</code> — whether a made draft gets SENT
                            without review. Only releases packs for organisations already marked
                            verified, and never releases one with outstanding lint violations.
                        </Text>
                    </Space>
                </Space>
            </Card>

            {autoApproval && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    style={{ marginBottom: 16 }}
                    message="Packs can send without a human reading them"
                    description="The approval gate carries two jobs and only one of them is automatable. Quality review is; identity and affiliation verification is not. A pack contains brand-voice customer email copy and live survey links, so in the wrong hands it is a phishing kit and the damage lands on BrandScience. Auto-approval is restricted to organisations an admin has already verified for exactly that reason."
                />
            )}

            <Card title="LLM provider — primary + fallback" size="small" style={{ marginBottom: 16 }}>
                <ProviderModelTempForm
                    label="Primary"
                    value={primaryValue}
                    onChange={handlePrimaryChange}
                    anthropicModels={anthropicModels}
                    openaiModels={openaiModels}
                    googleModels={googleModels}
                />
                <div style={{ marginTop: 12 }}>
                    <ProviderModelTempForm
                        label="Fallback"
                        value={fallbackValue}
                        onChange={handleFallbackChange}
                        anthropicModels={anthropicModels}
                        openaiModels={openaiModels}
                        googleModels={googleModels}
                    />
                </div>
            </Card>

            <Card style={{ marginBottom: 16 }}>
                <Tabs
                    tabPosition="left"
                    style={{ minHeight: 500 }}
                    items={AUTO_CONCIERGE_KEYS.map((type) => {
                        const row = findRow(type.configKey);
                        return {
                            key: type.configKey,
                            label: (
                                <span>
                                    {row ? (
                                        <Badge dot status="success" offset={[-4, 0]}>
                                            <span>{type.label}</span>
                                        </Badge>
                                    ) : (
                                        <span>{type.label}</span>
                                    )}
                                </span>
                            ),
                            children: (
                                <RowEditor
                                    row={row}
                                    onChange={(patch) => upsertRow(type.configKey, patch)}
                                    topAlert={
                                        <Alert
                                            type="info"
                                            showIcon
                                            icon={<InfoCircleOutlined />}
                                            style={{ marginBottom: 16 }}
                                            message={type.label}
                                            description={type.description}
                                        />
                                    }
                                    placeholdersAlert={PLACEHOLDERS}
                                    extraAlert={
                                        type.configKey === 'covering_email' ? COVERING_TOKENS : null
                                    }
                                />
                            ),
                        };
                    })}
                />
            </Card>

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: 16 }}
                message="Rules here are backed by a lint, not by the prompt alone"
                description="Every draft is checked mechanically before it reaches the queue, and a violation blocks approval. When one fires, the rule text and the offending excerpt are fed back to whichever prompt broke it for a single corrective pass. So a banned phrasing does not need restating here to take effect, and adding it here will not catch what the lint does not."
            />

            <Paragraph type="secondary">
                The research prompt is not on this page. <code>commercial_profile</code> runs
                inside the hypothesis crawl and writes to the shared research subject, so it lives
                on The Hypothesis AI. Operational numbers are not here either: the incentive
                ceiling, daily cap, research reuse window and the sender user id are edited in
                System Settings, so they can be changed without publishing a new version of this
                subsystem.
            </Paragraph>
        </div>
    );
};

interface RowEditorProps {
    row: AutoConciergeAIConfigRow | undefined;
    onChange: (patch: Partial<AutoConciergeAIConfigRow>) => void;
    topAlert: React.ReactNode;
    placeholdersAlert: React.ReactNode;
    extraAlert: React.ReactNode;
}

const RowEditor: React.FC<RowEditorProps> = ({
    row,
    onChange,
    topAlert,
    placeholdersAlert,
    extraAlert,
}) => {
    return (
        <div>
            {topAlert}

            {row && (
                <Space style={{ marginBottom: 12 }} wrap>
                    <Tag color={isActiveBool(row.is_active) ? 'green' : 'default'}>
                        {isActiveBool(row.is_active) ? 'active' : 'inactive'}
                    </Tag>
                    <Switch
                        checked={isActiveBool(row.is_active)}
                        onChange={(checked) => onChange({ is_active: checked ? 1 : 0 })}
                        checkedChildren="on"
                        unCheckedChildren="off"
                    />
                </Space>
            )}

            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="auto_concierge_ai"
                    label="System Prompt"
                    value={row?.system_prompt ?? ''}
                    onChange={(v) => onChange({ system_prompt: v })}
                    rows={8}
                />
            </div>
            <div style={{ marginBottom: 16 }}>
                <PromptEditor
                    consumerSubsystem="auto_concierge_ai"
                    label="User Prompt Template"
                    value={row?.user_prompt_template ?? ''}
                    onChange={(v) => onChange({ user_prompt_template: v })}
                    rows={14}
                />
            </div>

            <Alert
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                message="Available Placeholders"
                description={placeholdersAlert}
            />

            {extraAlert && (
                <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    style={{ marginTop: 12 }}
                    message="Tokens the rendered email must carry"
                    description={extraAlert}
                />
            )}
        </div>
    );
};
